import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import DashboardSidebar from "../../components/DashboardSidebar";
import { checkIsLoggedIn, handleFetchResponse } from "./helpers";
import { BACKEND_URL } from "../../utils/config.js";
import "./Dashboard.css";

const FRONTEND_URL = process.env.REACT_APP_FRONTEND_URL || "/backend";

export default function OpenAppointments() {
    const navigate = useNavigate();
    const [user] = useState({
        username: localStorage.getItem('username') || '',
        user_id: localStorage.getItem('user_id') || '',
        role: localStorage.getItem('role') || ''
    });
    const [appointments, setAppointments] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
    const [acceptingAppointmentId, setAcceptingAppointmentId] = useState(null);
    const [successMessage, setSuccessMessage] = useState(null);

    useEffect(() => {
        const fetchAppointments = async () => {
            try {
                const isLoggedIn = await checkIsLoggedIn();
                if (!isLoggedIn) {
                    navigate(`${FRONTEND_URL}/login`);
                    localStorage.clear();
                    return;
                }

                // Verify that user is a PSW
                const userRole = localStorage.getItem('role');
                if (userRole !== 'psw') {
                    navigate(`${FRONTEND_URL}/service-worker-dashboard`);
                    return;
                }

                const res = await fetch(`${BACKEND_URL}/appointments/available`, {
                    method: "GET",
                    headers: {
                        "Content-Type": "application/json",
                    },
                    credentials: "include",
                });

                const checkedRes = await handleFetchResponse(res, navigate);
                if (!checkedRes) return;

                if (!checkedRes.ok) {
                    throw new Error(`Failed to fetch appointments: ${checkedRes.status}`);
                }

                const data = await checkedRes.json();
                setAppointments(data.appointments || data || []);
            } catch (err) {
                setError(err.message);
                console.error("Error fetching appointments:", err);
            } finally {
                setLoading(false);
            }
        };

        fetchAppointments();
    }, [navigate, BACKEND_URL]);

    const handleLogout = async () => {
        try {
            await fetch(`${BACKEND_URL}/users/logout`, {
                method: "POST",
                credentials: "include",
            });
        } catch (err) {
            console.error("Logout error:", err);
        }
        localStorage.clear();
        navigate(`${FRONTEND_URL}/login`);
    };

    const formatDateTime = (isoString) => {
        if (!isoString) return { date: 'N/A', time: 'N/A' };
        try {
            const dateObj = new Date(isoString);
            if (isNaN(dateObj.getTime())) {
                return { date: 'Invalid Date', time: 'Invalid Time' };
            }
            const date = dateObj.toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' });
            const time = dateObj.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit', hour12: true });
            return { date, time };
        } catch (error) {
            console.error('Error formatting date:', error, isoString);
            return { date: 'N/A', time: 'N/A' };
        }
    };

    const getServiceNames = (appointment) => {
        if (appointment.service_names && Array.isArray(appointment.service_names)) {
            return appointment.service_names.join(', ');
        }
        if (appointment.service_name) {
            return appointment.service_name;
        }
        if (appointment.service) {
            return appointment.service;
        }
        return 'N/A';
    };

    const handleAcceptAppointment = async (appointmentId) => {
        const confirmed = window.confirm('Are you sure you want to accept this appointment?');
        if (!confirmed) {
            return;
        }

        try {
            setAcceptingAppointmentId(appointmentId);
            const res = await fetch(`${BACKEND_URL}/appointments/take/${appointmentId}`, {
                method: "PUT",
                headers: {
                    "Content-Type": "application/json",
                },
                credentials: "include",
            });

            const checkedRes = await handleFetchResponse(res, navigate);
            if (!checkedRes) return;

            if (!checkedRes.ok) {
                const errorData = await checkedRes.json().catch(() => ({}));
                throw new Error(errorData.message || `Failed to accept appointment: ${checkedRes.status}`);
            }

            // Remove the accepted appointment from the list
            setAppointments(appointments.filter(apt => apt.appointment_id !== appointmentId));
            setSuccessMessage('Appointment accepted successfully!');
            setTimeout(() => setSuccessMessage(null), 3000);
        } catch (err) {
            setError(err.message);
            console.error("Error accepting appointment:", err);
        } finally {
            setAcceptingAppointmentId(null);
        }
    };

    return (
        <div className="dashboard-page">
            <DashboardSidebar
                sidebarCollapsed={sidebarCollapsed}
                onToggleSidebar={() => setSidebarCollapsed(!sidebarCollapsed)}
                mobileMenuOpen={mobileMenuOpen}
                onToggleMobileMenu={() => setMobileMenuOpen(!mobileMenuOpen)}
                profileTitle={`Service Worker${user.username ? ` - ${user.username}` : ''}`}
                profileSubtitle="Accept available appointments"
                buttons={[
                    {
                        icon: '👤',
                        text: 'Back to Dashboard',
                        onClick: () => {
                            navigate(`${FRONTEND_URL}/service-worker-dashboard`);
                            setMobileMenuOpen(false);
                        },
                        ariaLabel: 'Back to dashboard',
                        title: 'Back to Dashboard'
                    },
                    {
                        icon: '🚪',
                        text: 'Logout',
                        onClick: handleLogout,
                        className: 'logout-button',
                        ariaLabel: 'Logout',
                        title: 'Logout'
                    }
                ]}
            />

            <div className="dashboard-main">
                <section className="dashboard-card">
                    <div className="dashboard-title-row">
                        <h3>Available Appointments</h3>
                        <small>{appointments.length} available</small>
                    </div>

                    {successMessage && (
                        <div style={{
                            padding: '12px 16px',
                            backgroundColor: '#dcfce7',
                            color: '#166534',
                            borderRadius: '6px',
                            marginBottom: '16px',
                            border: '1px solid #86efac'
                        }}>
                            {successMessage}
                        </div>
                    )}

                    {error && (
                        <div style={{
                            padding: '12px 16px',
                            backgroundColor: '#fee2e2',
                            color: '#991b1b',
                            borderRadius: '6px',
                            marginBottom: '16px',
                            border: '1px solid #fca5a5'
                        }}>
                            {error}
                        </div>
                    )}

                    {loading ? (
                        <div style={{ padding: '40px', textAlign: 'center', color: '#64748b' }}>
                            Loading appointments...
                        </div>
                    ) : appointments.length === 0 ? (
                        <div style={{ padding: '40px', textAlign: 'center', color: '#64748b' }}>
                            No available appointments at this time.
                        </div>
                    ) : (
                        <div style={{ display: 'grid', gap: '16px' }}>
                            {appointments.map((apt) => {
                                const { date, time } = formatDateTime(apt.start_date);
                                return (
                                    <div
                                        key={apt.appointment_id}
                                        style={{
                                            padding: '16px',
                                            border: '1px solid #e2e8f0',
                                            borderRadius: '8px',
                                            backgroundColor: '#f8fafc',
                                            display: 'grid',
                                            gridTemplateColumns: '1fr auto',
                                            gap: '16px',
                                            alignItems: 'start'
                                        }}
                                    >
                                        <div>
                                            <div style={{ marginBottom: '12px' }}>
                                                <h4 style={{ margin: '0 0 8px 0', color: '#1e293b' }}>
                                                    {apt.patient_name || 'Patient Name Not Available'}
                                                </h4>
                                                <div style={{ display: 'grid', gap: '6px', fontSize: '14px' }}>
                                                    <div>
                                                        <strong>Date & Time:</strong> {date} at {time}
                                                    </div>
                                                    <div>
                                                        <strong>Services:</strong> {getServiceNames(apt)}
                                                    </div>
                                                    {apt.description && (
                                                        <div>
                                                            <strong>Description:</strong> {apt.description}
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                        <button
                                            onClick={() => handleAcceptAppointment(apt.appointment_id)}
                                            disabled={acceptingAppointmentId === apt.appointment_id}
                                            style={{
                                                padding: '10px 20px',
                                                backgroundColor: acceptingAppointmentId === apt.appointment_id ? '#cbd5e1' : '#10b981',
                                                color: 'white',
                                                border: 'none',
                                                borderRadius: '6px',
                                                cursor: acceptingAppointmentId === apt.appointment_id ? 'not-allowed' : 'pointer',
                                                fontSize: '14px',
                                                fontWeight: '600',
                                                whiteSpace: 'nowrap',
                                                transition: 'background-color 0.2s'
                                            }}
                                            onMouseEnter={(e) => {
                                                if (acceptingAppointmentId !== apt.appointment_id) {
                                                    e.target.style.backgroundColor = '#059669';
                                                }
                                            }}
                                            onMouseLeave={(e) => {
                                                if (acceptingAppointmentId !== apt.appointment_id) {
                                                    e.target.style.backgroundColor = '#10b981';
                                                }
                                            }}
                                        >
                                            {acceptingAppointmentId === apt.appointment_id ? 'Accepting...' : 'Accept'}
                                        </button>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </section>
            </div>
        </div>
    );
}
